import { Box } from '@mui/material'
import React, { useEffect } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { CertificateValidityPeriod } from 'src/enums'
import { setToMidnight } from 'src/utils'
import { z } from 'zod'
import { useGetCustomerSuppliers } from '../../api/supplier'
import BasicDatePicker from '../../components/DateTimePicker'
import { AppForm, AppFormProps, FormFileCoreUpload, FormSelectField, FormTextField } from '../../components/FormCore'
import { GenericDialogLayout } from '../../components/GenericDialogLayout'
import { MainButton } from '../../components/MainButton'
import { SuppliersFormField } from '../../components/SuppliersFormField'
import { APIResultCount } from '../../constant'
import { ICustomerAdminSupplier } from '../../generated-types'
import { ICertificate } from '../../generated-types/Certificate'
import { ICertificateDocumentType } from '../../generated-types/certificate-document-type'
import { EnumToSelectTypes } from '../../generated-types/select-type'
import { useZodForm } from '../../hooks/zod-form'

/**
 * Calculate the valid until date based on certificate validity period
 * @param certificateDate - The certificate issuance date
 * @param validityPeriod - The validity period type from CertificateValidityPeriod enum
 * @returns The calculated valid until date, or undefined for Custom period
 */
const calculateValidUntilDate = (
  certificateDate: Date | undefined,
  validityPeriod: CertificateValidityPeriod | undefined,
): Date | undefined => {
  if (!certificateDate || !validityPeriod || validityPeriod === CertificateValidityPeriod.Custom) {
    return undefined
  }

  const validUntilDate = new Date(certificateDate)

  switch (validityPeriod) {
    case CertificateValidityPeriod.oneYear:
      validUntilDate.setFullYear(validUntilDate.getFullYear() + 1)
      break
    case CertificateValidityPeriod.twoYear:
      validUntilDate.setFullYear(validUntilDate.getFullYear() + 2)
      break
    case CertificateValidityPeriod.threeYear:
      validUntilDate.setFullYear(validUntilDate.getFullYear() + 3)
      break
    case CertificateValidityPeriod.EighteenMonths:
      validUntilDate.setMonth(validUntilDate.getMonth() + 18)
      break
    case CertificateValidityPeriod.TwentyYears:
      validUntilDate.setFullYear(validUntilDate.getFullYear() + 20)
      break
    default:
      return undefined
  }

  return validUntilDate
}

interface ICreateCertificateFormProps extends Omit<AppFormProps<ICertificate>, 'onSubmit'> {
  customerId: EntityId
  title: string
  onSubmit: (data: any, formContext?: UseFormReturn<any>) => void
}
export const CreateCertificateForm: React.FC<ICreateCertificateFormProps> = ({
  onSubmit,
  onCancel,
  customerId,
  title,
}) => {
  const { data: suppliers } = useGetCustomerSuppliers(customerId, 0, APIResultCount.Max)

  const { t: translate } = useTranslation(['admin-customer', 'common', 'certificate'])
  const schema = z
    .object({
      certificateNumber: z
        .string()
        .nonempty()
        .regex(/^(?! *$).+/, translate('certificateNumberIsInvalid', { ns: 'certificate' })),
      certificateDate: z.date(),
      validPeriod: z.object({
        id: z.number(),
      }),
      certificateDocumentType: z
        .object({
          id: z.number(),
        })
        .transform(value => value.id),
      fileId: z.union([z.string(), z.number()]),
      supplierId: z
        .object({
          id: z.number(),
        })
        .transform(value => value.id),
      ValidUntil: z.date().optional(),
    })
    .superRefine(({ validPeriod, ValidUntil, certificateDate }, ctx) => {
      // Rule 1: ValidUntil is required for Custom period
      if (validPeriod?.id === CertificateValidityPeriod.Custom && !ValidUntil) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: translate('required'),
          path: ['ValidUntil'],
        })
      }

      // Rule 2: ValidUntil must be >= certificateDate
      if (ValidUntil && certificateDate && ValidUntil < certificateDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: translate('ValidUntilLessThanCertificateDate', { ns: 'certificate' }),
          path: ['ValidUntil'],
        })
      }

      // Rule 3: ValidUntil cannot be in the past
      if (ValidUntil && ValidUntil < setToMidnight(new Date())) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: translate('ValidUntilCannotBePastDate', { ns: 'certificate' }),
          path: ['ValidUntil'],
        })
      }
    })

  const form = useZodForm(schema)

  const certificateDocumentTypes = EnumToSelectTypes(ICertificateDocumentType, 'certificate')
  const validPeriods = EnumToSelectTypes(CertificateValidityPeriod, 'certificate')

  const validPeriod = form.watch('validPeriod')
  const validUntil = form.watch('ValidUntil')
  const certificateDate = form.watch('certificateDate')

  // Auto-calculate ValidUntil when validPeriod or certificateDate changes
  useEffect(() => {
    if (validPeriod?.id && validPeriod.id !== CertificateValidityPeriod.Custom && certificateDate) {
      const calculatedDate = calculateValidUntilDate(certificateDate, validPeriod.id)
      if (calculatedDate) {
        form.setValue('ValidUntil', calculatedDate, { shouldValidate: true })
      }
    } else if (validPeriod?.id !== CertificateValidityPeriod.Custom) {
      form.setValue('ValidUntil', undefined, { shouldValidate: true })
      form.clearErrors('ValidUntil')
    }
  }, [validPeriod?.id, certificateDate])

  useEffect(() => {
    if (validUntil) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      const validUntilDate = new Date(validUntil)
      validUntilDate.setHours(0, 0, 0, 0)

      if (validUntilDate < today) {
        form.setError('certificateDate', {
          type: 'manual',
          message: translate('certificateExpired', {
            ns: 'main-component',
            date: `${validUntilDate.getFullYear()}-${validUntilDate.getMonth() + 1}-${validUntilDate.getDate()}`,
          }),
        })
      } else {
        form.clearErrors('certificateDate')
      }
    }
  }, [validUntil, form, translate])

  return (
    <GenericDialogLayout
      title={title}
      pop={onCancel}
      removedCloseButton={false}
      actions={[
        <MainButton
          key="cert-submit"
          onClick={() => {
            form.handleSubmit(data => onSubmit(data, form))()
          }}
        >
          {translate('add', { ns: 'common' })}
        </MainButton>,
      ]}
    >
      <AppForm form={form} onSubmit={data => onSubmit(data, form)}>
        <FormTextField
          fullWidth
          name="certificateNumber"
          label={translate('certificateNumber', { ns: 'certificate' })}
        />
        <BasicDatePicker label={translate('certificateDate', { ns: 'certificate' })} name="certificateDate" />

        <FormSelectField
          name="validPeriod"
          data={validPeriods}
          label={translate('selectDuration', { ns: 'certificate' })}
          getOptionLabel={option => option.value}
          renderOption={(props: any, option) => (
            <Box {...props} key={option.id}>
              {option.value}
            </Box>
          )}
          isOptionEqualToValue={(o, v) => o.id === v.id}
        />

        <BasicDatePicker
          label={translate('validUntil', { ns: 'certificate' })}
          name="ValidUntil"
          disabled={validPeriod?.id !== CertificateValidityPeriod.Custom}
        />

        <FormSelectField
          name="certificateDocumentType"
          data={certificateDocumentTypes}
          label={translate('documentType', { ns: 'certificate' })}
          getOptionLabel={option => option.value}
          renderOption={(props: any, option) => (
            <Box {...props} key={option.id}>
              {option.value}
            </Box>
          )}
          isOptionEqualToValue={(o, v) => o.id === v.id}
        />
        <SuppliersFormField
          suppliers={(suppliers?.items as ICustomerAdminSupplier[]) || []}
          name="supplierId"
          label={translate('certifiedBy', { ns: 'certificate' })}
        />
        <FormFileCoreUpload
          label={translate('fileName', { ns: 'common' })}
          name="fileId"
          mapFileKey="id"
          height="160px"
          acceptedFileTypes={[
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'image/jpg',
            'image/jpeg',
            'image/png',
          ]}
          showFileTypes={false}
        />
      </AppForm>
    </GenericDialogLayout>
  )
}
